import React, { useMemo, useState } from "react";
import {
  DropdownList,
  type DropdownListGroup,
  type IDropdownListItem,
} from "@/components/ui/dropdown-list/dropdown-list";
import { nodeVariableToGroups, type NodeVariable } from "../../types";
import { NodeTypes } from "@/workflow_api/types/workflow";

export interface VariableSelectionData {
  value: string; // fieldId o nodeId para variables globales
  connection?: string; // nodeId de origen (no se usa para variables globales)
  field?: string; // fieldId (solo para campos de formulario)
  global_type?:
    | "APPLICANT"
    | "NODE_EXECUTION_START"
    | "NODE_EXECUTION_FINISH"
    | "CURRENT_TIME";
  ref_name?: string;
  output_name?: string;
}

interface VariableSelectorProps {
  variables: NodeVariable[];
  selectedData?: {
    value: string;
    connection?: string;
    field?: string;
    global_type?:
      | "APPLICANT"
      | "NODE_EXECUTION_START"
      | "NODE_EXECUTION_FINISH"
      | "CURRENT_TIME";
    output_name?: string;
  };
  type?: string;
  onUpdateElement: (data: VariableSelectionData) => void;
}

/**
 * Selector de variables para destinatarios de notificaciones
 * Filtra y muestra solo variables de tipo string
 */
export const VariableSelector: React.FC<VariableSelectorProps> = ({
  variables,
  selectedData,
  type,
  onUpdateElement,
}) => {
  const [variableSearch, setVariableSearch] = useState("");
  const [_selectedVariable, setSelectedVariable] =
    useState<IDropdownListItem>();
  const [_selectedGroup, setSelectedGroup] = useState<DropdownListGroup>();

  const variableGroups = useMemo(() => {
    const groups = nodeVariableToGroups(variables);

    return groups
      .map((group) => ({
        ...group,
        items: group.items.filter((item) => {
          let mappedType = "";
          switch (type) {
            case "STRING":
              mappedType = "string";
              break;
            case "DATE":
              mappedType = "date";
              break;
            case "FLOAT":
            case "NUMBER":
            case "INTEGER":
              mappedType = "number";
              break;
            default:
              mappedType = "";
          }
          return (
            item.detail === mappedType &&
            ((item.label as string)
              .toLowerCase()
              .includes(variableSearch.toLowerCase()) ||
              group.groupLabels.some((label) =>
                label.toLowerCase().includes(variableSearch.toLowerCase()),
              ))
          );
        }),
      }))
      .filter((group) => group.items.length > 0);
  }, [variables, variableSearch, type]);

  // Grupos sin filtrar para búsqueda de preselección
  const allGroups = useMemo(() => nodeVariableToGroups(variables), [variables]);

  // Sincronizar variable seleccionada cuando selectedData cambie
  React.useEffect(() => {
    console.log(selectedData);
    if (!selectedData?.value) {
      setSelectedVariable(undefined);
      setSelectedGroup(undefined);
      return;
    }

    // Primero buscar la variable en las variables originales (sin filtrar por tipo)
    let foundVariableId: string | undefined;
    let foundNodeId: string | undefined;

    if (selectedData.global_type) {
      // Variable global: buscar por global_type y value
      for (const node of variables) {
        const originalVar = node.variables.find((v) => {
          if (v.global_type !== selectedData.global_type) return false;
          if (
            selectedData.global_type === "APPLICANT" ||
            selectedData.global_type === "CURRENT_TIME"
          ) {
            return v.id === selectedData.value;
          }
          return v.node_id === selectedData.value;
        });
        if (originalVar) {
          foundVariableId = originalVar.id;
          foundNodeId = node.nodeId;
          break;
        }
      }
    } else if (selectedData?.field && selectedData?.output_name) {
      foundVariableId = `${selectedData.field}-${selectedData.output_name}`;
      foundNodeId = selectedData.connection;
    } else if (selectedData.field) {
      // Variable de formulario: buscar por field
      foundVariableId = selectedData.field;
      foundNodeId = selectedData.connection;
    } else if (selectedData.output_name) {
      foundVariableId = `${selectedData.connection}-${selectedData.output_name}`;
      foundNodeId = selectedData.connection;
    } else if (selectedData.connection) {
      // Variable de nodo (switch/calculated): buscar donde v.id === value
      foundVariableId = selectedData.value;
      foundNodeId = selectedData.connection;
    } else {
      // Fallback: buscar por value directamente
      foundVariableId = selectedData.value;
    }

    if (!foundVariableId) return;

    // Ahora buscar en variableGroups (filtrado por tipo) para la UI
    let foundVariable: IDropdownListItem | undefined;
    let foundGroup: DropdownListGroup | undefined;

    // Primero intentar en el grupo específico
    if (foundNodeId) {
      foundGroup = variableGroups.find((g) => g.groupId === foundNodeId);
      if (foundGroup) {
        foundVariable = foundGroup.items.find(
          (item) => item.value === foundVariableId,
        );
      }
    }

    // Si no encontramos, buscar en todos los grupos
    if (!foundVariable) {
      for (const group of variableGroups) {
        foundVariable = group.items.find(
          (item) => item.value === foundVariableId,
        );
        if (foundVariable) {
          foundGroup = group;
          break;
        }
      }
    }

    // Si aún no encontramos en variableGroups, buscar en allGroups (sin filtrar)
    // y usar eso para mostrar al menos algo
    if (!foundVariable && allGroups.length > 0) {
      for (const group of allGroups) {
        foundVariable = group.items.find(
          (item) => item.value === foundVariableId,
        );
        if (foundVariable) {
          foundGroup = group;
          break;
        }
      }
    }

    if (foundVariable && foundGroup) {
      setSelectedVariable(foundVariable);
      setSelectedGroup(foundGroup);
    }
  }, [selectedData, variableGroups, variables, allGroups]);

  const onVariableSelection = (variableId: string, groupId: string) => {
    const group = variableGroups.find((g) => g.groupId === groupId);
    const variable = group?.items.find((item) => item.value === variableId);

    // Buscar la variable original para obtener el node_id y global_type
    const sourceNode = variables.find((v) => v.nodeId === groupId);
    const originalVariable = sourceNode?.variables.find(
      (v) => v.id === variableId,
    );
    // Detectar si es una variable global
    const globalType = originalVariable?.global_type;

    if (globalType) {
      // Variable global: usar value con nodeId y global_type
      // Para NODE_EXECUTION_START/FINISH: value = nodeId del nodo a monitorear
      // Para APPLICANT: value = nombre de la propiedad (usamos el nombre de la variable)
      const globalValue =
        globalType === "APPLICANT" || globalType === "CURRENT_TIME"
          ? originalVariable?.id || ""
          : originalVariable?.node_id || groupId;

      onUpdateElement({
        value: globalValue,
        global_type: globalType,
      });
    } else {
      // Usar node_id de la variable original si existe, sino usar groupId
      const connectionNodeId =
        originalVariable?.node_id || sourceNode?.nodeId || groupId;

      // Detectar si es una "switch variable" (variable que no viene de un campo de formulario)
      // Estas variables tienen id === node_id
      const isSwitchVariable =
        originalVariable?.id === originalVariable?.node_id;
      if (originalVariable?.field_id && originalVariable?.output_name) {
        onUpdateElement({
          value: originalVariable?.output_name || "",
          connection: connectionNodeId,
          ref_name: sourceNode?.ref_name || "",
          field: originalVariable.field_id,
          output_name: originalVariable.output_name,
        });
      } else if (
        sourceNode?.ref_name === NodeTypes.N8N ||
        sourceNode?.ref_name === NodeTypes.INTEROPERABILITY ||
        sourceNode?.ref_name === NodeTypes.EXPEDIENTE
      ) {
        onUpdateElement({
          value: originalVariable?.output_name || "",
          connection: connectionNodeId,
          ref_name: sourceNode?.ref_name || "",
          output_name: originalVariable?.output_name,
        });
      } else {
        onUpdateElement({
          value: variable?.value || "",
          connection: connectionNodeId,
          ref_name: sourceNode?.ref_name || "",
          // Solo incluir field si NO es una switch variable (viene de un campo real)
          field: isSwitchVariable ? undefined : variable?.value || "",
        });
      }
    }
    setSelectedVariable(variable);
    setSelectedGroup(group);
  };

  return (
    <DropdownList
      group="right"
      options={variableGroups}
      selectedValue={_selectedVariable}
      selectedGroup={_selectedGroup}
      toggleSelection={(variable, group) => {
        onVariableSelection(variable.value, group?.groupId || "");
      }}
      widthClassName="flex-1"
      placeholder="Elegir variable"
      search={{
        placeholder: "Buscar variable",
        onSearch: (query) => {
          setVariableSearch(query);
        },
        query: variableSearch,
      }}
    />
  );
};
